import React, { useCallback, useEffect, useState, useContext } from "react";
import { Box, Typography } from "@material-ui/core";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";
import {
  CreateOrUpdateUnitImageInfoproperty,
  GetAllPropertyImagesCategoryByPropertyId,
} from "../../../../../Services";
import { getDownloadableLink } from "../../../../../Helper";
import { AddNewPropertyImageDialog } from "../../../NewPropertiesView/PropertyOverview/UI/Gallery/UI";
import {
  CompleteProfileImagesSkeleton,
  CustomButton,
  CustomCard,
  CustomCheckbox,
  NoDataFound,
} from "../../../../../Components";
import Divider from "../CPP_PropertyDetailsComponent/Divider";

// icons
import { IconImageOffLine, PlusIcon } from "../../../../../assets/icons";

// styles
import useStyles from "./styles";
import { CompletePropertyProfileContext } from "../../../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext";

function ImageWithFallback({
  src,
  alt,
  className,
  fallbackClass,
  fallbackIcon,
  ...rest
}) {
  const [error, setError] = useState(false);
  return (
    <>
      {!error && src ? (
        <img
          src={src}
          alt={alt}
          className={className}
          loading="lazy"
          onError={() => setError(true)}
          {...rest}
        />
      ) : null}
      {(error || !src) && <Box className={fallbackClass}>{fallbackIcon}</Box>}
    </>
  );
}

function CPP_ImagesComponent({ propertyId, stepId, onStepComplete }) {
  const styles = useStyles();
  const { translate } = useTranslate("CompletePropertyProfile");
  const { setAlertBoxContent } = useVerticalNav();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const context = useContext(CompletePropertyProfileContext);

  const [images, setImages] = useState([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [refetchDataPropertyImage, setRefetchDataPropertyImage] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddNewImage = () => {
    setIsUploadDialogOpen(true);
  };

  const GetAllImages = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await GetAllPropertyImagesCategoryByPropertyId(propertyId);
      setImages(res);
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("failed-to-fetch-images"),
        onClose: () => setAlertBoxContent(null),
      });
      console.error("Error fetching images:", error);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, setAlertBoxContent, translate]);

  const ImagePublishHandler = async (img, category) => {
    setIsLoading(true);
    try {
      const body = {
        ...img,
        isPublished: !img.isPublished,
      };
      await CreateOrUpdateUnitImageInfoproperty(body);

      setAlertBoxContent({
        display: true,
        variant: "success",
        title: !img.isPublished
          ? translate("image-published-successfully")
          : translate("image-unpublished-successfully"),
        onClose: () => setAlertBoxContent(null),
      });

      GetAllImages();
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("image-publish-failed"),
        onClose: () => setAlertBoxContent(null),
      });
      console.error("Error updating image:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = useCallback(() => {
    if (images.length === 0) {
      return { 
        isValid: false, 
        errors: [{ message: translate('please-add-at-least-one-image') }] 
      };
    }
    return { isValid: true, errors: [] };
  }, [images.length, translate]);

  const onSaveClicked = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: validation.errors[0].message,
        onClose: () => setAlertBoxContent(null),
      });
      return { success: false, errors: validation.errors };
    }
    
    if (onStepComplete) {
      onStepComplete();
    }
    
    setAlertBoxContent({
      display: true,
      variant: 'success',
      title: translate('images-saved-successfully'),
      onClose: () => setAlertBoxContent(null),
    });
    return { success: true };
  }, [validateForm, onStepComplete, setAlertBoxContent, translate]);

  const onNextClicked = useCallback(async () => {
    return await onSaveClicked();
  }, [onSaveClicked]);

  useEffect(() => {
    if (context?.setSaveHandler) context.setSaveHandler(onSaveClicked);
    if (context?.setNextHandler) context.setNextHandler(onNextClicked);
    if (context?.setValidationHandler) context.setValidationHandler(validateForm);

    return () => {
      if (context?.setSaveHandler) context.setSaveHandler(null);
      if (context?.setNextHandler) context.setNextHandler(null);
      if (context?.setValidationHandler) context.setValidationHandler(null);
    };
  }, [onSaveClicked, onNextClicked, validateForm, context]);

  useEffect(() => {
    GetAllImages();
  }, [GetAllImages]);

  useEffect(() => {
    if (refetchDataPropertyImage) {
      GetAllImages();
      setRefetchDataPropertyImage(false);
    }
  }, [GetAllImages, refetchDataPropertyImage]);

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography className={styles.title}>{translate("images")}</Typography>
        <CustomButton
          variant="text"
          boxShadow="none"
          hideHoverBg
          onClick={() => handleAddNewImage()}
          startIcon={
            <PlusIcon
              width="20"
              height="20"
              fill={palette.button.tertiaryGray_fg}
            />
          }
        >
          <Typography className={styles.textBtn}>
            {translate("add-new-image")}
          </Typography>
        </CustomButton>
      </Box>
      <Divider />
      <Box className={styles.imagesContainer}>
        {isLoading ? (
          Array.from({ length: 10 }).map((_, index) => (
            <CompleteProfileImagesSkeleton key={index} />
          ))
        ) : images.length > 0 ? (
          images.map((category) =>
            category.images.result.map((img) => {
              return (
                <CustomCard className={styles.cardStyle}>
                  <Box key={img.fileId} className={styles.mainContainer}>
                    <ImageWithFallback
                      src={img?.fileId ? getDownloadableLink(img.fileId) : null}
                      alt={img?.fileName || "Main"}
                      className={styles.imageBox}
                      fallbackClass={styles.imageFallback}
                      fallbackIcon={
                        <IconImageOffLine
                          width="55"
                          height="55"
                          fill={palette.foreground.quinary}
                        />
                      }
                    />
                    <Box className={styles.detailsContainer}>
                      <Typography className={styles.detailsText}>
                        {category.categoryName}
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: "8px" }}
                      >
                        <CustomCheckbox
                          checked={img?.isPublished}
                          onChange={() => ImagePublishHandler(img, category)}
                        />
                        <Typography className={styles.detailsText}>
                          {img?.isPublished
                            ? translate("unpublish")
                            : translate("publish")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CustomCard>
              );
            })
          )
        ) : (
          <NoDataFound title={translate("images")} />
        )}
      </Box>
      {isUploadDialogOpen && (
        <AddNewPropertyImageDialog
          onOpen={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          propretyId={propertyId}
          setRefetchDataPropertyImage={setRefetchDataPropertyImage}
          onImageAdded={() => {
            // Refresh images after upload
            setRefetchDataPropertyImage(true);
          }}
        />
      )}
    </Box>
  );
}

export default CPP_ImagesComponent;
